import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './useScroller.module.css';

type ScrollerArgs = {
  byRow: boolean;
  gapWidth: number;
};

type ScrollerReturn<
  RefElement extends HTMLElement = HTMLDivElement,
  RefItemElement extends HTMLElement = HTMLDivElement,
> = {
  calculateNextLeft: () => number;
  calculateNextRight: () => number;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  /** Classnames to apply a fade gradient to the scrollable element. */
  classNames: Record<string, boolean>;
  /** Push a ref for each item within the scrollable element into this collection. */
  refs: MutableRefObject<(RefItemElement | null)[]>;
  /** Attach this ref to the element that will be horizontally scrolled. */
  scrollElementRef: MutableRefObject<RefElement | null>;
};

export function useScroller<
  RefElement extends HTMLElement = HTMLDivElement,
  RefItemElement extends HTMLElement = HTMLDivElement,
>(
  { byRow, gapWidth }: ScrollerArgs = {
    byRow: false,
    gapWidth: 8,
  }
): ScrollerReturn<RefElement, RefItemElement> {
  const scrollElementRef = useRef<RefElement>(null);
  const refs = useRef<(RefItemElement | null)[]>([]);

  const [scrollPosition, setScrollPosition] = useState(0);
  const [sliderContainerWidth, setSliderContainerWidth] = useState(0);

  const calculateNextRight = () => {
    const scrollElement = scrollElementRef.current;

    let left = 0;
    let ind = 0;
    const scrollLeft = scrollElement?.scrollLeft || 0;
    if (byRow) {
      while (
        left <= scrollLeft + scrollElement!.clientWidth &&
        ind < refs.current.length
      ) {
        const width = refs.current[ind]?.clientWidth || 0;
        left += width + gapWidth;
        ind++;
      }
      left -= refs.current[ind]?.clientWidth || 0;
      left -= gapWidth;
    } else {
      while (left <= scrollLeft && ind < refs.current.length) {
        const width = refs.current[ind]?.clientWidth || 0;
        left += width + gapWidth;
        ind++;
      }
    }
    // need 1 because scrollTo is not definite
    return left + 1;
  };

  const calculateNextLeft = () => {
    const scrollElement = scrollElementRef.current;

    let left = 0;
    let ind = 0;
    const scrollLeft = scrollElement?.scrollLeft || 0;
    const width = refs.current[ind]?.clientWidth || 0;
    let targetLeft = 0;
    // if you have less than the screen width, go to 0
    if (scrollLeft - scrollElement!.clientWidth - 2 <= 0) {
      return 0;
    }
    if (byRow) {
      while (true) {
        ind++;

        const currentWidth = refs.current[ind]?.clientWidth || 0;

        if (
          left + currentWidth + gapWidth >
            scrollLeft - scrollElement!.clientWidth - 2 ||
          ind >= refs.current.length
        ) {
          left += currentWidth + gapWidth;
          break;
        }
        left += currentWidth + gapWidth;
      }
      targetLeft = left;
    } else {
      left += width + gapWidth;

      while (left <= scrollLeft - 2 && ind < refs.current.length - 1) {
        ind++;

        const currentWidth = refs.current[ind]?.clientWidth || 0;

        targetLeft = left;
        left += currentWidth + gapWidth;
      }
    }

    // need 1 because scrollTo is not definite
    return targetLeft - 1;
  };

  const canScrollRight = useMemo(() => {
    const scrollElement = scrollElementRef.current;

    const width = scrollElement?.clientWidth || 0;
    const scrollWidth = scrollElement?.scrollWidth || 0;

    return (
      sliderContainerWidth >= width && scrollPosition < scrollWidth - width - 50
    );
  }, [sliderContainerWidth, scrollPosition, scrollElementRef]);

  const canScrollLeft = useMemo(() => {
    return scrollPosition >= 50;
  }, [scrollPosition]);

  const classNames = useMemo(() => {
    return {
      [styles['fade--both']]: canScrollLeft && canScrollRight,
      [styles['fade--left']]: canScrollLeft && !canScrollRight,
      [styles['fade--right']]: !canScrollLeft && canScrollRight,
    };
  }, [canScrollLeft, canScrollRight]);

  const onScroll = useCallback(() => {
    const scrollElement = scrollElementRef.current;
    setScrollPosition(scrollElement?.scrollLeft || 0);
  }, [scrollElementRef]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;
    function updateSize() {
      setSliderContainerWidth(scrollElement?.offsetWidth || 0);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [setSliderContainerWidth, sliderContainerWidth, refs.current.length]);

  useEffect(() => {
    const scrollEl = scrollElementRef.current;
    if (!scrollEl) return;

    scrollEl.addEventListener('scroll', onScroll);

    return () => {
      return scrollEl.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  return {
    calculateNextLeft,
    calculateNextRight,
    canScrollLeft,
    canScrollRight,
    classNames,
    refs,
    scrollElementRef,
  };
}
